import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import { InputLabel } from "tap/components/InputLabel";
import { ClearableFilterInput } from "tap/components/ClearableFilterInput";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { useWindowsData } from "../../hooks/useWindowsData";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
import {
  buildFilterFromParams,
  cleanWindowsFilter,
} from "./utils/windowsSearchUtils";
import "./styles.scss";

/**
 * Search form for filtering the Windows (Window Sequences) grid.
 *
 * Available filters:
 * - Window ID: exact ID search
 * - Window Sequence Name: text search on sequence name
 * - Users: multi-select for assigned users
 * - Status: multi-select for window status
 *
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay).
 */
export function WindowsSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const {
    fetchWindows,
    updateWindows,
    windowsRange,
    windowsSort,
    filterForm,
    itemsPerPage,
  } = useWindowsData();
  const { updateWindowsSearchForm, clearWindows } = useWindowsActions();
  const { userOptions, statusOptions } = useSelector((s) => s.windows);

  const [validationErrors, setValidationErrors] = useState({
    windowId: undefined,
    windowSequenceName: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    try {
      const userRes = await tapApi.users(
        { dataType: "windowSequence" },
        [0, 199],
        ["fullName", "ASC"],
      );

      if (isMounted()) {
        const users =
          userRes?.content?.map(({ fullName, id }) => ({
            label: fullName,
            value: id,
          })) ?? [];
        updateWindows({
          userOptions: users,
        });
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        updateWindows({ userOptions: [] });
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const filterData = buildFilterFromParams(
      params,
      userOptions,
      statusOptions,
    );

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateWindows({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    if (hasUrlParams) {
      updateWindowsSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when options load (multi-select values depend on options being available)
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasMultiSelectParams = params.users || params.status;

    if (
      hasMultiSelectParams &&
      (userOptions.length > 0 || statusOptions.length > 0)
    ) {
      const filterData = {
        ...filterForm,
        ...buildFilterFromParams(params, userOptions, statusOptions),
      };
      updateWindowsSearchForm({ key: "filterForm", data: filterData });
      fetchData(filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, statusOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchWindows(range, filterData);
    if (data) {
      updateWindows({
        windowSequences: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        windowsRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL; the location.key useEffect handles re-fetch
  const handleResetFilter = () => {
    clearWindows();
    setValidationErrors({
      windowId: undefined,
      windowSequenceName: undefined,
    });
    navigate(location.pathname);
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (["windowId", "windowSequenceName"].includes(field)) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateWindowsSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanWindowsFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          windowsSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  const handleClearField = (field) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newValidationErrors = { ...validationErrors, [field]: undefined };
    setValidationErrors(newValidationErrors);

    const newFilterForm = { ...filterForm, [field]: "" };
    updateWindowsSearchForm({
      key: "filterForm",
      data: { [field]: "" },
    });

    const resetRange = getItemRange(0, itemsPerPage);
    const filtered = cleanWindowsFilter(
      newFilterForm,
      resetRange,
      windowsSort,
    );
    updateSearchParams(location, navigate, filtered);
    fetchData(newFilterForm, resetRange);
  };

  return (
    <Row className="windows-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Window ID">
          <ClearableFilterInput
            value={filterForm?.windowId || ""}
            onChange={(e) => handleInputChange("windowId", e.target.value)}
            onClear={() => handleClearField("windowId")}
            width="auto"
            id="windowId"
            name="windowId"
            placeholder="Enter"
            className={validationErrors.windowId ? "input-error" : ""}
          />
          {validationErrors.windowId && (
            <div className="validation-error">{validationErrors.windowId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Window Sequence Name">
          <ClearableFilterInput
            value={filterForm?.windowSequenceName || ""}
            onChange={(e) =>
              handleInputChange("windowSequenceName", e.target.value)
            }
            onClear={() => handleClearField("windowSequenceName")}
            width="auto"
            id="windowSequenceName"
            name="windowSequenceName"
            placeholder="Enter"
            className={validationErrors.windowSequenceName ? "input-error" : ""}
          />
          {validationErrors.windowSequenceName && (
            <div className="validation-error">
              {validationErrors.windowSequenceName}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Users">
          <MultiSelect
            onChange={(value) => handleInputChange("users", value)}
            name="users"
            options={userOptions}
            isSearchable
            isClearable
            value={filterForm.users || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Status">
          <MultiSelect
            onChange={(value) => handleInputChange("status", value)}
            name="status"
            options={statusOptions}
            isSearchable
            isClearable
            value={filterForm.status || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
